import React from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { lightTheme, darkTheme } from "../styles/theme";
import { RandomFloatingIcons, FRONTEND_ICONS } from "./TechStackIcons";

const AboutSection = styled.section<{ theme: "light" | "dark" }>`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 70px 5% 40px;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) =>
    theme === "light" ? "#ffffff" : darkTheme.surface};

  /* Cross-hatch pattern */
  background-image: ${({ theme }) =>
    theme === "light"
      ? `
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 60px,
            rgba(77, 159, 255, 0.06) 60px,
            rgba(77, 159, 255, 0.06) 62px
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 60px,
            rgba(77, 159, 255, 0.06) 60px,
            rgba(77, 159, 255, 0.06) 62px
          )
        `
      : `
          repeating-linear-gradient(
            0deg,
            transparent,
            transparent 80px,
            rgba(255, 255, 255, 0.025) 80px,
            rgba(255, 255, 255, 0.025) 81px
          ),
          repeating-linear-gradient(
            90deg,
            transparent,
            transparent 80px,
            rgba(255, 255, 255, 0.025) 80px,
            rgba(255, 255, 255, 0.025) 81px
          )
        `};

  /* Corner decorative squares */
  &::before {
    content: "";
    position: absolute;
    top: 10%;
    left: 5%;
    width: 95px;
    height: 95px;
    background: ${({ theme }) =>
      theme === "light" ? `rgba(77, 159, 255, 0.07)` : `transparent`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(77, 159, 255, 0.18)`
          : `rgba(255, 255, 255, 0.05)`};
    transform: rotate(-8deg);
    pointer-events: none;
    z-index: 0;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 15%;
    right: 7%;
    width: 105px;
    height: 105px;
    background: ${({ theme }) =>
      theme === "light" ? `rgba(77, 159, 255, 0.06)` : `transparent`};
    border: 3px solid
      ${({ theme }) =>
        theme === "light"
          ? `rgba(77, 159, 255, 0.16)`
          : `rgba(255, 255, 255, 0.04)`};
    transform: rotate(12deg);
    pointer-events: none;
    z-index: 0;
  }

  @media (min-width: 768px) {
    padding: 70px 10% 40px;
  }
`;

const ContentWrapper = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)<{ theme: "light" | "dark" }>`
  font-size: 1.5rem;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.text : darkTheme.background};
  margin-bottom: 1.2rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  display: inline-block;
  padding: 8px 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.accent : darkTheme.accent};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  transform: rotate(-2deg);

  @media (min-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 1024px) {
    font-size: 2rem;
    margin-bottom: 1.8rem;
  }
`;


const AboutCard = styled(motion.div)<{ theme: "light" | "dark" }>`
  padding: 16px;
  background: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  transition: all 0.3s ease;

  &:hover {
    transform: translate(-3px, -3px);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  }

  @media (min-width: 768px) {
    padding: 20px;
  }

  @media (min-width: 1024px) {
    padding: 24px;
  }
`;

const AboutText = styled.div<{ theme: "light" | "dark" }>`
  p {
    margin-bottom: 0.7rem;
    line-height: 1.5;
    font-size: 0.85rem;
    color: ${({ theme }) =>
      theme === "light" ? lightTheme.textSecondary : darkTheme.textSecondary};
    font-weight: 500;

    @media (min-width: 768px) {
      font-size: 0.9rem;
      margin-bottom: 0.9rem;
    }

    @media (min-width: 1024px) {
      font-size: 0.95rem;
    }

    strong {
      color: ${({ theme }) =>
        theme === "light" ? lightTheme.text : darkTheme.text};
      font-weight: 900;
    }
  }

  p:last-child {
    margin-bottom: 0;
  }
`;


const HighlightBox = styled(motion.div)<{
  theme: "light" | "dark";
  color: string;
}>`
  padding: 14px;
  background: ${({ color }) => color};
  border: 4px solid
    ${({ theme }) => (theme === "light" ? lightTheme.border : darkTheme.border)};
  box-shadow: ${({ theme }) =>
    theme === "light" ? lightTheme.shadow : darkTheme.shadow};
  margin-top: 15px;
  transform: rotate(1deg);
  transition: all 0.3s ease;

  &:hover {
    transform: rotate(-1deg) scale(1.02);
    box-shadow: ${({ theme }) =>
      theme === "light" ? lightTheme.shadowLarge : darkTheme.shadowLarge};
  }

  @media (min-width: 768px) {
    padding: 18px;
    margin-top: 20px;
  }

  @media (min-width: 1024px) {
    padding: 20px;
  }
`;

const HighlightText = styled.p<{ theme: "light" | "dark" }>`
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.4;
  color: ${({ theme }) =>
    theme === "light" ? lightTheme.background : darkTheme.background};
  position: relative;
  z-index: 1;
  margin: 0;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }

  @media (min-width: 1024px) {
    font-size: 1rem;
  }

  strong {
    font-weight: 900;
    text-decoration: underline;
    text-decoration-thickness: 3px;
  }
`;

const About: React.FC = () => {
  const { theme } = useTheme();

  return (
    <AboutSection id="about" theme={theme}>
      {/* Floating Tech Stack Icons */}
      <RandomFloatingIcons theme={theme} icons={FRONTEND_ICONS} count={3} />

      <ContentWrapper>
        <SectionTitle
          theme={theme}
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          About Me
        </SectionTitle>
        <AboutCard
          theme={theme}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <AboutText theme={theme}>
            <p>
              Hello! I'm <strong>Trinh Van Thuan</strong>, a{" "}
              <strong>Software Engineer</strong> with a strong background in
              building scalable systems and applications.
            </p>
            <p>
              I graduated with <strong>High Distinction</strong> from the{" "}
              <strong>
                University of Engineering and Technology - Vietnam National
                University
              </strong>{" "}
              with a Bachelor's degree in Information Technology, Honors
              Program.
            </p>
            <p>
              Throughout my career, I've developed expertise in multiple domains
              including <strong>blockchain technology</strong>, where I've built{" "}
              <strong>NFT marketplaces</strong> and{" "}
              <strong>multi-chain gaming ecosystems</strong>, and{" "}
              <strong>enterprise systems</strong>, where I've implemented{" "}
              <strong>AI-driven automation</strong> and{" "}
              <strong>microservices architectures</strong>.
            </p>
          </AboutText>
        </AboutCard>
        <HighlightBox
          theme={theme}
          color={theme === "light" ? lightTheme.accent : darkTheme.accent}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <HighlightText theme={theme}>
            My experience spans from <strong>leading engineering teams</strong>{" "}
            to developing <strong>complex distributed systems</strong>. I'm
            passionate about creating impactful software that solves real-world
            problems!
          </HighlightText>
        </HighlightBox>
      </ContentWrapper>
    </AboutSection>
  );
};

export default About;
